import { useEffect, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent, ReactNode } from 'react';
import type { BrandingSection } from '../../../wire';
import { useT } from '../../../messages';
import type { BlockSpec } from './specs';
import { viewOf, viewsOf } from './specs';
import { sectionName, viewLabel } from './names';

/**
 * **The outline is the page** (the mockup's rail note): the sections in
 * order, each by its friendly name and its view, the pinned booking band
 * marked. Click selects (and the canvas scrolls to it), drag reorders, and the
 * keyboard does both — arrows move between rows, Enter selects, Alt+↑/↓ moves
 * the row itself.
 *
 * A listbox with a roving tab stop: one Tab into the list, arrows inside it,
 * one Tab out. The row that holds the stop is the selected one, else the first.
 */
export function Outline({
  sections,
  specs,
  pinned,
  selected,
  hovered,
  hidden,
  offPage,
  label,
  onSelect,
  onHover,
  onMove,
}: {
  sections: readonly BrandingSection[];
  specs: Map<string, BlockSpec>;
  pinned: string;
  selected: string | null;
  hovered: string | null;
  /** Keys the canvas drew nothing for — a band with nothing to show yet. */
  hidden: ReadonlySet<string>;
  /** Keys whose block does not belong on this page. */
  offPage: ReadonlySet<string>;
  label: string;
  onSelect: (key: string) => void;
  onHover: (key: string | null) => void;
  onMove: (from: number, to: number) => void;
}): ReactNode {
  const t = useT();
  const list = useRef<HTMLUListElement>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  /** A key to put focus back on after the list re-renders (a keyboard move). */
  const refocus = useRef<string | null>(null);

  const keys = sections.map((s) => s.id ?? s.type);
  const stop = focusKey !== null && keys.includes(focusKey) ? focusKey : (selected ?? keys[0]);

  useEffect(() => {
    const key = refocus.current;
    if (key === null) return;
    refocus.current = null;
    rowOf(list.current, key)?.focus();
  });

  const focusRow = (key: string | undefined): void => {
    if (key === undefined) return;
    setFocusKey(key);
    rowOf(list.current, key)?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLLIElement>, index: number): void => {
    const key = keys[index];
    if (key === undefined) return;
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const to = index + (e.key === 'ArrowUp' ? -1 : 1);
      if (to < 0 || to >= keys.length) return;
      refocus.current = key;
      setFocusKey(key);
      onMove(index, to);
      return;
    }
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        focusRow(keys[Math.max(0, index - 1)]);
        return;
      case 'ArrowDown':
        e.preventDefault();
        focusRow(keys[Math.min(keys.length - 1, index + 1)]);
        return;
      case 'Home':
        e.preventDefault();
        focusRow(keys[0]);
        return;
      case 'End':
        e.preventDefault();
        focusRow(keys[keys.length - 1]);
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(key);
        return;
      default:
        return;
    }
  };

  /** Before or after the row under the pointer, as a position in the list. */
  const slotOf = (e: DragEvent<HTMLLIElement>, index: number): number => {
    const box = e.currentTarget.getBoundingClientRect();
    return e.clientY > box.top + box.height / 2 ? index + 1 : index;
  };

  const drop = (): void => {
    if (dragging !== null && dropAt !== null) {
      // `dropAt` is a gap between rows; removing the row first shifts the gaps below it.
      const to = dropAt > dragging ? dropAt - 1 : dropAt;
      if (to !== dragging) onMove(dragging, to);
    }
    setDragging(null);
    setDropAt(null);
  };

  if (sections.length === 0) {
    return <p className="bld-hint">{t('builder.outline.empty')}</p>;
  }

  return (
    <ul
      ref={list}
      className="bld-outline"
      role="listbox"
      aria-label={label}
      aria-describedby="bld-outline-keys"
      onMouseLeave={() => onHover(null)}
    >
      {sections.map((section, index) => {
        const key = keys[index] ?? section.type;
        const spec = specs.get(section.type);
        const views = viewsOf(spec);
        const view = views.length > 1 ? viewOf(spec, section.slots) : '';
        const isPinned = section.type === pinned;
        const classes = [
          'bld-outline__row',
          dragging === index ? 'is-dragging' : '',
          dropAt === index ? 'drop-before' : '',
          dropAt === index + 1 && index === sections.length - 1 ? 'drop-after' : '',
          hovered === key ? 'is-hovered' : '',
          hidden.has(key) ? 'is-quiet' : '',
        ]
          .filter((c) => c !== '')
          .join(' ');
        return (
          <li
            key={key}
            data-key={key}
            role="option"
            aria-selected={selected === key}
            tabIndex={stop === key ? 0 : -1}
            className={classes}
            draggable
            onClick={() => {
              setFocusKey(key);
              onSelect(key);
            }}
            onFocus={() => setFocusKey(key)}
            onKeyDown={(e) => onKeyDown(e, index)}
            onMouseEnter={() => onHover(key)}
            onDragStart={(e) => {
              setDragging(index);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', key);
            }}
            onDragOver={(e) => {
              if (dragging === null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDropAt(slotOf(e, index));
            }}
            onDrop={(e) => {
              e.preventDefault();
              drop();
            }}
            onDragEnd={() => {
              setDragging(null);
              setDropAt(null);
            }}
          >
            <span className="bld-outline__grip" aria-hidden="true">
              ⋮⋮
            </span>
            <span className="bld-outline__name">
              {sectionName(t, section.type, spec)}
              {view === '' ? null : (
                <span className="bld-outline__view"> · {viewLabel(t, view)}</span>
              )}
            </span>
            <span className="bld-outline__marks">
              {isPinned ? <span className="bld-mark">{t('builder.outline.pinned')}</span> : null}
              {offPage.has(key) ? (
                <span className="bld-mark bld-mark--bad">{t('builder.outline.offPage')}</span>
              ) : null}
              {hidden.has(key) && !offPage.has(key) ? (
                <span className="bld-mark">{t('builder.outline.notShowing')}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** The row for a key — compared as data, never spliced into a selector. */
function rowOf(root: HTMLElement | null, key: string): HTMLElement | undefined {
  if (root === null) return undefined;
  return [...root.querySelectorAll<HTMLElement>('[data-key]')].find(
    (el) => el.dataset['key'] === key,
  );
}
